import { isPlainObject } from "@/helpers/inspect";

// Style values are stored with CSS units ("480px") but edited as plain numbers.
export const parseCssNumber = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseCssText = (style) =>
  String(style || "")
    .split(";")
    .filter(Boolean)
    .reduce((acc, curr) => {
      const [cssKey, cssValue] = curr.split(":");
      acc[cssKey.trim()] = cssValue;
      return acc;
    }, {});

const isSameShallow = (a, b) => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
};

// Turns the stored value into numbers for the form inputs. Accepts both the object
// form ({ width: "480px" }) and the legacy CSS text ("width:480px;height:270px").
// Only keys described by the schema are touched, so values the form does not render
// (e.g. an admin-set "margin") survive the round trip untouched.
export const cssStyleToNumbers = (style, properties = {}) => {
  const source = isPlainObject(style) ? style : parseCssText(style);

  const result = Object.entries(source).reduce((acc, [cssKey, cssValue]) => {
    acc[cssKey] = cssKey in properties ? parseCssNumber(cssValue) : cssValue;
    return acc;
  }, {});

  // Already-normalized input is returned as the same reference. useStyle() assigns the
  // result during render, and handing back a fresh object every time would retrigger
  // reactivity and spin the render loop.
  return isPlainObject(style) && isSameShallow(style, result) ? style : result;
};

const hasStyleField = (scheme) =>
  Object.values(scheme?.properties || {}).some(
    (prop) => prop?.type === "style" || (prop?.type === "list" && hasStyleField(prop.items))
  );

// Brings every style field of a settings payload to the object form the meta schema
// expects. Entries the user never reopened may still hold the legacy CSS text, which
// would fail validation on save.
export const normalizeSchemeStyles = (data, scheme) => {
  const properties = scheme?.properties || {};

  // Schemas without style fields are passed through untouched.
  if (!hasStyleField(scheme)) {
    return data;
  }

  return Object.entries(data).reduce((acc, [key, value]) => {
    const prop = properties[key];

    if (prop?.type === "style") {
      acc[key] = numbersToCssStyle(prop.properties, cssStyleToNumbers(value, prop.properties));
    } else if (prop?.type === "list" && Array.isArray(value)) {
      acc[key] = value.map((item) =>
        isPlainObject(item) ? normalizeSchemeStyles(item, prop.items) : item
      );
    } else {
      acc[key] = value;
    }

    return acc;
  }, {});
};

// The meta schema expects an object of CSS strings: { width: "480px", height: "270px" }.
export const numbersToCssStyle = (properties, values) => {
  const rest = isPlainObject(values) ? { ...values } : {};
  Object.keys(properties).forEach((cssKey) => delete rest[cssKey]);

  return Object.entries(properties).reduce((acc, [cssKey, prop]) => {
    const value = values[cssKey];

    acc[cssKey] =
      !value || value === "" || value === 0
        ? "inherit"
        : `${parseCssNumber(value)}${prop.cssValue}`;

    return acc;
  }, rest);
};
